import { Fragment, useRef } from "react";

import {
  AssistantInfo,
  Header,
  Footer,
  SystemResponse,
  UserResponse,
  SystemResponseProps,
  HeaderActionProps,
  UserResponseProps,
} from "../";
import { AutoScrollProvider } from "../../contexts";
import { useAutoScrollWithResize } from "../../hooks";
import { OriginSwitch } from "../OriginSwitch";
import { Container, Dialog, Status } from "./styled";
import { useChatClass, useTheme } from "../../theme";

export interface Turn {
  id: string;
  type: "user" | "system";
  props: SystemResponseProps | UserResponseProps;
}

export interface SystemTurn extends Turn {
  type: "system";
  /** Distinguishes chatbot messages from human operator messages. */
  source?: "bot" | "agent";
  /** Canned bot replies (e.g. "already chatting with a team member") set this so
   * they get no "Chatbot online" divider and do not count as the bot taking over. */
  suppressOriginSwitch?: boolean;
  props: SystemResponseProps;
}

export interface UserTurn extends Turn {
  type: "user";
  props: UserResponseProps;
}

const OPERATOR_ONLINE_LABEL = "Team member online";
const BOT_ONLINE_LABEL = "Chatbot online";

/** Divider label to show above each turn where the business side changed
 * hands: an operator's first reply after the bot, or the bot's first reply
 * after an operator. Worked out from the history, so it survives a reload. */
function getHandoffDividers(
  turns: Array<SystemTurn | UserTurn>,
): Map<string, string> {
  const dividers = new Map<string, string>();
  let previousBusinessSource: SystemTurn["source"];

  turns.forEach((turn) => {
    if (turn.type === "system" && !turn.suppressOriginSwitch) {
      const operatorJoined =
        turn.source === "agent" && previousBusinessSource !== "agent";
      const botReturned =
        turn.source !== "agent" && previousBusinessSource === "agent";
      if (operatorJoined) {
        dividers.set(turn.id, OPERATOR_ONLINE_LABEL);
      } else if (botReturned) {
        dividers.set(turn.id, BOT_ONLINE_LABEL);
      }
      previousBusinessSource = turn.source;
    }
  });

  return dividers;
}

type ChatProps = {
  /** Chat Widget name (usually company name). Show in Header & AssistantInfo */
  name: string;
  /** Publicly accessible URL. Image shown in Header, AssistantInfo, and Avatar next to every message */
  image: string;
  /** Shown below the "image" when customers first open the chat widget.
   * This is an introductions of sorts. You can include instructions for
   * interacting with the chat widget, promote a tour, or anything else. */
  description: string;
  /** Powered by TOMIS shown or not */
  watermark: boolean;
  /** Icons rendered in the header (restart, minimize, etc.) */
  headerActions: HeaderActionProps[];
  /** Callback to execute to restart the session */
  restartSession: () => Promise<void>;
  /** Callback to execute to send a message */
  handleSend: (text: string) => Promise<boolean>;
  /** Called as the user types in the composer (each keystroke). Used to publish
   * live-chat typing presence. */
  onType?: () => void;
  /** All messages sent by chatbot and customer */
  turns: Array<SystemTurn | UserTurn>;
  /** Has the conversation ended? No current use case for this  */
  hasEnded?: boolean;
  /** Show `<TypingIndicator />` below messages, to indicate the
   * chatbot is typing a response. */
  indicator?: boolean;
  /** Render additional components below the footer */
  extra?: React.ReactNode;
  /** Optional callback when the "Powered by" link is clicked */
  onPoweredByClick?: () => void;
  /** Company name shown in the "Powered by" watermark (e.g., "TOMIS", "Yonder") */
  poweredByName?: string;
  /** URL for the "Powered by" link (ideally with UTM params) */
  poweredByUrl?: string;
  /** If true, hide the footer (composer/watermark) */
  hideFooter?: boolean;
  /** Banner at the top of the open chat. Used for a status update. */
  banner?: React.ReactNode;
};

/** The entire **OPEN** chat widget */
export const Chat: React.FC<ChatProps> = ({
  description,
  image,
  name,
  watermark = true,
  headerActions = [],
  hasEnded = false,
  handleSend,
  onType,
  restartSession,
  turns = [],
  indicator = false,
  extra,
  onPoweredByClick,
  poweredByName,
  poweredByUrl,
  hideFooter = false,
  banner,
}: ChatProps) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const chatClass = useChatClass();

  // Auto-scroll to bottom when turns change or typing indicator appears
  useAutoScrollWithResize(dialogRef, [turns.length, indicator], {
    threshold: 300,
    resizeBehavior: "smooth",
    initialBehavior: "auto",
  });

  const handoffDividers = getHandoffDividers(turns);

  return (
    <Container
      className={chatClass("window-inner")}
      role="region"
      aria-labelledby="chat-widget-title"
    >
      <Header title={name} image={image} actions={headerActions} />
      <Dialog
        className={chatClass("dialog-container")}
        ref={dialogRef}
        role="log"
        aria-live="polite"
        aria-atomic="false"
        aria-relevant="additions"
        aria-label="Chat conversation messages"
      >
        <AutoScrollProvider target={dialogRef}>
          {banner}
          <AssistantInfo
            title={name}
            avatar={image}
            description={description}
          />
          {turns.map((turn, turnIndex) => {
            if (turn.type === "system") {
              return (
                <Fragment key={turn.id}>
                  {handoffDividers.has(turn.id) ? (
                    <OriginSwitch label={handoffDividers.get(turn.id)} />
                  ) : null}
                  <SystemResponse
                    {...turn.props}
                    isLast={turnIndex === turns.length - 1}
                  />
                </Fragment>
              );
            } else if (turn.type === "user") {
              return <UserResponse {...turn.props} key={turn.id} />;
            } else {
              return null;
            }
          })}
          {indicator && (
            <SystemResponse.Indicator
              avatar={image}
              containerStyle={{ marginTop: theme.space[5] }}
            />
          )}
          {hasEnded && (
            <Status role="status" aria-live="polite">
              You have ended the chat
            </Status>
          )}
        </AutoScrollProvider>
      </Dialog>
      {!hideFooter ? (
        <Footer
          withWatermark={watermark}
          hasEnded={hasEnded}
          onStart={restartSession}
          onSend={handleSend}
          onType={onType}
          onPoweredByClick={onPoweredByClick}
          poweredByName={poweredByName}
          poweredByUrl={poweredByUrl}
        />
      ) : null}
      {extra}
    </Container>
  );
};
